// src/hooks/useCompanySettingsQuery.js
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useContext } from 'react';
import companyService from '../services/companyService';
import { AuthContext } from '../context/AuthContext';

// Query key for company settings
const COMPANY_SETTINGS_KEY = ['companySettings'];

/**
 * React Query hook for fetching and caching company settings
 * This ensures company data is fetched only once and shared across all components
 */
export const useCompanySettingsQuery = (alwaysPublic = false) => {
    const authContext = useContext(AuthContext);
    const isAuthenticated = authContext?.isAuthenticated ?? false;

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: [...COMPANY_SETTINGS_KEY, isAuthenticated, alwaysPublic],
        queryFn: async () => {
            // Use public endpoint if not authenticated or if alwaysPublic is true
            if (!isAuthenticated || alwaysPublic) {
                return await companyService.getPublicCompanyInfo();
            } else {
                try {
                    // Try authenticated endpoint first
                    return await companyService.getCompanySettings();
                } catch (err) {
                    // Fallback to public endpoint if authenticated request fails
                    console.warn('Authenticated company settings request failed, falling back to public:', err);
                    return await companyService.getPublicCompanyInfo();
                }
            }
        },
        staleTime: 1000 * 60 * 30, // Consider data fresh for 30 minutes
        cacheTime: 1000 * 60 * 60, // Keep in cache for 1 hour
        retry: 1,
        refetchOnWindowFocus: false,
        refetchOnMount: false, // Don't refetch if data exists in cache
        placeholderData: keepPreviousData, // Keep old settings visible while auth state changes
    });

    return {
        settings: data || null,
        loading: isLoading,
        error: error?.message || null,
        refresh: refetch,
        // Convenient accessors
        logo: data?.logo || null,
        icon: data?.icon || null,
        name: data?.name || 'Company Name'
    };
};

// Convenience hooks that use the same cached data
export const useCompanyLogo = (alwaysPublic = false) => {
    const { logo, loading, error, refresh } = useCompanySettingsQuery(alwaysPublic);
    return { logo, loading, error, refresh };
};

export const useCompanyIcon = (alwaysPublic = false) => {
    const { icon, loading, error, refresh } = useCompanySettingsQuery(alwaysPublic);
    return { icon, loading, error, refresh };
};

export const useCompanyName = (alwaysPublic = false) => {
    const { name, loading, error, refresh } = useCompanySettingsQuery(alwaysPublic);
    return { name, loading, error, refresh };
};

// For backward compatibility, export the main hook as useCompanySettings
export const useCompanySettings = useCompanySettingsQuery;